import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthUser, logout as apiLogout, me, setOn401Handler } from "../services/api";

const KRATOS = process.env.REACT_APP_KRATOS_PUBLIC_URL || "http://localhost:4433";

const returnTo = () => encodeURIComponent(`${window.location.origin}/`);

export type AuthProblem =
  | { kind: "revoked"; message: string }
  | { kind: "unreachable"; message: string };

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  problem: AuthProblem | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  startRegistration: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<AuthProblem | null>(null);

  // Register 401 handler so axios interceptor can clear auth state
  useEffect(() => {
    setOn401Handler(() => {
      setUser(null);
      setProblem(null);
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const userData = await me();
      setUser(userData);
      setProblem(null);

      // After successful login, if we're still on the auth page, redirect to home
      if (window.location.pathname.startsWith("/auth/")) {
        window.history.replaceState(null, "", "/");
      }
    } catch (err: unknown) {
      setUser(null);

      const response =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number; data?: { detail?: string } } }).response
          : undefined;

      // 401 is simply "not signed in" and needs no banner. A 403 means the
      // identity is valid but the account was disabled by an administrator.
      if (response?.status === 403) {
        setProblem({
          kind: "revoked",
          message:
            response.data?.detail ||
            "Your access has been revoked. Please contact your administrator.",
        });
      } else if (!response) {
        setProblem({
          kind: "unreachable",
          message: "Cannot reach the server. Confirm the backend is running, then retry.",
        });
      } else {
        setProblem(null);
      }
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const logout = useCallback(async () => {
    // Clear local app session first
    try {
      await apiLogout();
    } catch {
      // The app session may already be gone; continue anyway
    }

    // Clear user state immediately
    setUser(null);
    setProblem(null);

    // Try Kratos logout if available (for Kratos-based auth)
    try {
      const res = await fetch(`${KRATOS}/self-service/logout/browser`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        const data = (await res.json()) as { logout_url?: string };
        if (data.logout_url) {
          window.location.href = data.logout_url;
          return;
        }
      }
    } catch {
      // Kratos unavailable; use local logout
    }

    // For local auth or Kratos unavailable: redirect to home
    window.location.href = "/";
  }, []);

  const startRegistration = () => {
    window.location.href = `${KRATOS}/self-service/registration/browser?return_to=${returnTo()}`;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, problem, refresh, logout, startRegistration }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth requires AuthProvider");
  return ctx;
}
